import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse, ArkifiHubCheckoutRequestDto, ArkifiHubCheckoutResponseDto, PickupLocationDto, ShoppingCardPaymentResponseDto, PaymentMethod, VerifyArkifiHubCheckoutResponseDto, ArkifiHubOrderHistoryDto, SavedDeliveryAddressDto, UpdateSavedDeliveryAddressRequestDto } from '../models/checkout.dto';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  private apiUrl = `${environment.apiUrl}/api/arkifihubcheckout`;
  private pickupApiUrl = `${environment.apiUrl}/api/PickUpLocation`;
  private arkifiHubPickupApiUrl = `${environment.apiUrl}/api/ArkifiHubStoreLocation`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private settingsService: SettingsService
  ) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      console.warn('No authentication token found. User may need to log in again.');
    }
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  /**
   * Get all pickup locations for ArkifiHub users (JWT authenticated)
   */
  getPickupLocations(platformId?: string): Observable<ApiResponse<PickupLocationDto[]>> {
    const url = platformId 
      ? `${this.arkifiHubPickupApiUrl}?platformId=${platformId}`
      : this.pickupApiUrl;
    
    const options = platformId 
      ? { headers: this.getAuthHeaders() }
      : {};
    
    return this.http.get<ApiResponse<PickupLocationDto[]>>(url, options);
  }

  /**
   * Get pickup locations by state
   */
  getPickupLocationsByState(state: string, platformId?: string): Observable<ApiResponse<PickupLocationDto[]>> {
    const url = platformId 
      ? `${this.arkifiHubPickupApiUrl}/state/${state}?platformId=${platformId}`
      : `${this.pickupApiUrl}/state/${state}`;
    
    const options = platformId 
      ? { headers: this.getAuthHeaders() }
      : {};
    
    return this.http.get<ApiResponse<PickupLocationDto[]>>(url, options);
  }

  /**
   * Get available states for pickup locations
   */
  getAvailableStates(platformId?: string): Observable<ApiResponse<string[]>> {
    const url = platformId 
      ? `${this.arkifiHubPickupApiUrl}/states?platformId=${platformId}`
      : `${this.pickupApiUrl}/states`;
    
    const options = platformId 
      ? { headers: this.getAuthHeaders() }
      : {};
    
    return this.http.get<ApiResponse<string[]>>(url, options);
  }

  /**
   * Get specific pickup location details
   */
  getPickupLocation(id: string): Observable<ApiResponse<PickupLocationDto>> {
    return this.http.get<ApiResponse<PickupLocationDto>>(`${this.pickupApiUrl}/${id}`);
  }

  /**
   * Process shopping card checkout
   */
  processShoppingCardCheckout(checkoutData: ArkifiHubCheckoutRequestDto): Observable<ApiResponse<ShoppingCardPaymentResponseDto>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', checkoutData.platformId)
      .set('environment', environment);

    return this.http.post<ApiResponse<ShoppingCardPaymentResponseDto>>(
      `${this.apiUrl}/shopping-card`, 
      checkoutData, 
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }

  /**
   * Process payment gateway checkout
   */
  processPaymentGatewayCheckout(checkoutData: ArkifiHubCheckoutRequestDto): Observable<ApiResponse<ArkifiHubCheckoutResponseDto>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', checkoutData.platformId)
      .set('environment', environment);

    return this.http.post<ApiResponse<ArkifiHubCheckoutResponseDto>>(
      `${this.apiUrl}/payment-gateway`, 
      checkoutData, 
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }

  /**
   * Process checkout (automatically routes to appropriate payment method)
   */
  processCheckout(checkoutData: ArkifiHubCheckoutRequestDto): Observable<ApiResponse<ArkifiHubCheckoutResponseDto | ShoppingCardPaymentResponseDto>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', checkoutData.platformId)
      .set('environment', environment);

    return this.http.post<ApiResponse<ArkifiHubCheckoutResponseDto | ShoppingCardPaymentResponseDto>>(
      this.apiUrl, 
      checkoutData, 
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }

  /**
   * Check if logistics provider is configured for a platform
   */
  checkLogisticsProvider(platformId: string): Observable<ApiResponse<boolean>> {
    const url = `${environment.apiUrl}/api/ProviderKeySettings/logistics/check/${platformId}`;
    return this.http.get<ApiResponse<boolean>>(url, {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * Verify checkout transaction
   */
  verifyCheckout(transactionReference: string): Observable<ApiResponse<VerifyArkifiHubCheckoutResponseDto>> {
    const request = {
      transactionReference: transactionReference
    };

    return this.http.post<ApiResponse<VerifyArkifiHubCheckoutResponseDto>>(
      `${this.apiUrl}/verify`,
      request,
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Get order history for authenticated customer
   */
  getOrderHistory(platformId?: string, pageNumber: number = 1, pageSize: number = 20): Observable<ApiResponse<ArkifiHubOrderHistoryDto>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    
    if (platformId) {
      params = params.set('platformId', platformId);
    }

    return this.http.get<ApiResponse<ArkifiHubOrderHistoryDto>>(
      `${this.apiUrl}/order-history`,
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }

  /**
   * Get saved delivery addresses for the authenticated ArkifiHub user (by email, platform, environment).
   */
  getSavedDeliveryAddresses(email: string, platformId: string, environment: string): Observable<ApiResponse<SavedDeliveryAddressDto[]>> {
    const params = new HttpParams()
      .set('email', email)
      .set('platformId', platformId)
      .set('environment', environment);
    return this.http.get<ApiResponse<SavedDeliveryAddressDto[]>>(
      `${this.apiUrl}/saved-delivery-addresses`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * Update a saved delivery address (syncs to Terminal Africa + Arkifi DB).
   */
  updateSavedDeliveryAddress(email: string, platformId: string, environment: string, body: UpdateSavedDeliveryAddressRequestDto): Observable<ApiResponse<SavedDeliveryAddressDto>> {
    const params = new HttpParams()
      .set('email', email)
      .set('platformId', platformId)
      .set('environment', environment);
    return this.http.put<ApiResponse<SavedDeliveryAddressDto>>(
      `${this.apiUrl}/saved-delivery-addresses`,
      body,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * Delete a saved delivery address from Arkifi DB (soft delete).
   */
  deleteSavedDeliveryAddress(email: string, platformId: string, environment: string, providerAddressId: string): Observable<ApiResponse<boolean>> {
    const params = new HttpParams()
      .set('email', email)
      .set('platformId', platformId)
      .set('environment', environment)
      .set('providerAddressId', providerAddressId);
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/saved-delivery-addresses`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * Get Terminal Africa states for a country (default Nigeria). For delivery address dropdown.
   */
  getDeliveryStates(platformId: string): Observable<ApiResponse<TerminalAfricaStateItemDto[]>> {
    const env = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('countryCode', 'NG')
      .set('environment', env);
    return this.http.get<ApiResponse<TerminalAfricaStateItemDto[]>>(
      `${this.apiUrl}/arkifi/states`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * Get Terminal Africa cities for a state. For delivery address dropdown.
   */
  getDeliveryCities(platformId: string, stateCode: string): Observable<ApiResponse<TerminalAfricaCityItemDto[]>> {
    const env = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('countryCode', 'NG')
      .set('stateCode', stateCode)
      .set('environment', env);
    return this.http.get<ApiResponse<TerminalAfricaCityItemDto[]>>(
      `${this.apiUrl}/arkifi/cities`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * Get delivery quote from logistics provider
   */
  getDeliveryQuote(quoteRequest: DeliveryQuoteRequestDto, platformId: string): Observable<ApiResponse<DeliveryQuoteResponseDto>> {
    const env = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', env);

    return this.http.post<ApiResponse<DeliveryQuoteResponseDto>>(
      `${this.apiUrl}/delivery-quote`,
      quoteRequest,
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }
}

// DTOs for delivery quote
export interface DeliveryQuoteRequestDto {
  storeLocationId: string;
  deliveryAddress: string;
  totalAmount: number;
  packageWeight?: number;
}

export interface DeliveryQuoteResponseDto {
  success: boolean;
  message?: string;
  companies: DeliveryCompanyQuoteDto[];
}

export interface DeliveryCompanyQuoteDto {
  companyId: number;
  companyName: string;
  deliveryFee: number;
  estimatedDeliveryTime?: string;
  currency: string;
  carrierLogo?: string;
}

/** Terminal Africa state (for delivery address dropdown). */
export interface TerminalAfricaStateItemDto {
  name: string;
  isoCode: string;
  countryCode: string;
  latitude?: string;
  longitude?: string;
}

/** Terminal Africa city (for delivery address dropdown). */
export interface TerminalAfricaCityItemDto {
  name: string;
  countryCode: string;
  stateCode: string;
  latitude?: string;
  longitude?: string;
}
